import React, { useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const WA_NUMBER = '573158497136';
const WA_MESSAGE = encodeURIComponent(
  '¡Hola! 👋 Me interesa hablar sobre cómo Effsos puede ayudarme a automatizar mi negocio. ¿Podemos agendar una reunión?'
);

const ContactSection = () => {
  const form = useRef();
  const [snackbar, setSnackbar] = useState({ open: false, success: true });
  const [sending, setSending] = useState(false);

  const handleClose = () => setSnackbar({ ...snackbar, open: false });

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);
    emailjs
      .sendForm('service_g3ucr7m', 'template_nid4kd1', form.current, 'YCCmP3KgPpsENgm4N')
      .then(
        () => {
          setSnackbar({ open: true, success: true });
          form.current.reset();
        },
        () => setSnackbar({ open: true, success: false })
      )
      .finally(() => setSending(false));
  };

  return (
    <Box id="contact" sx={{ bgcolor: 'white', py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="overline"
            display="block"
            align="center"
            sx={{ color: '#38BDF8', fontWeight: 700, letterSpacing: 3, mb: 1 }}
          >
            HABLEMOS
          </Typography>
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 800, color: '#083a94', mb: 2, fontSize: { xs: '2rem', md: '2.6rem' } }}
          >
            ¿Tienes un negocio que automatizar?
          </Typography>
          <Typography
            align="center"
            sx={{ color: '#555', maxWidth: 520, mx: 'auto', mb: 8, fontSize: '1.05rem' }}
          >
            Cuéntanos de tu negocio. El primer paso es una conversación —
            sin compromisos, sin tecnicismos.
          </Typography>
        </motion.div>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">

          {/* Lado izquierdo - info */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ flex: '0 0 auto', width: '100%', maxWidth: 340 }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#083a94', mb: 1 }}>
                  ¿Cómo funciona?
                </Typography>
                <Typography sx={{ color: '#666', lineHeight: 1.8 }}>
                  Nos cuentas de tu negocio, coordinamos una reunión virtual o presencial,
                  analizamos juntos y te presentamos una propuesta concreta.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <CalendarTodayIcon sx={{ color: '#083a94', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, color: '#083a94' }}>Reunión inicial</Typography>
                </Stack>
                <Typography sx={{ color: '#666', fontSize: '0.9rem' }}>
                  Gratuita, sin compromiso. 30–45 minutos para entender tu operación.
                </Typography>
              </Box>

              <Divider />

              {/* CTA WhatsApp directo */}
              <Box>
                <Typography sx={{ fontWeight: 600, color: '#083a94', mb: 1.5 }}>
                  ¿Prefieres escribir directamente?
                </Typography>
                <Button
                  component="a"
                  href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  fullWidth
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#1ebe5d' },
                  }}
                >
                  Escribir por WhatsApp
                </Button>
              </Box>
            </Stack>
          </motion.div>

          {/* Formulario */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{ flex: 1, width: '100%' }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                border: '1px solid #E5E7EB',
                borderRadius: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#083a94', mb: 3 }}>
                Cuéntanos de tu proyecto
              </Typography>
              <form ref={form} onSubmit={sendEmail}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Nombre"
                      variant="outlined"
                      required
                    />
                    <TextField
                      fullWidth
                      name="email"
                      label="Correo electrónico"
                      variant="outlined"
                      type="email"
                      required
                    />
                  </Stack>
                  <TextField
                    fullWidth
                    name="business"
                    label="¿A qué se dedica tu negocio?"
                    variant="outlined"
                    placeholder="Ej: Taller mecánico, consultorio, tienda de ropa..."
                  />
                  <TextField
                    fullWidth
                    name="message"
                    label="¿Qué proceso quieres automatizar o mejorar?"
                    variant="outlined"
                    multiline
                    rows={4}
                    required
                    placeholder="Cuéntanos el problema o proceso que quieres resolver..."
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={sending}
                      endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                      sx={{
                        bgcolor: '#083a94',
                        fontWeight: 700,
                        px: 4,
                        py: 1.4,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#0d4dbf' },
                      }}
                    >
                      {sending ? 'Enviando...' : 'Agendar una reunión'}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Paper>
          </motion.div>
        </Stack>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={snackbar.success ? 'success' : 'error'} sx={{ width: '100%' }}>
          {snackbar.success
            ? '¡Mensaje enviado! Te contactamos pronto para coordinar la reunión.'
            : 'Ocurrió un error al enviar. Intenta de nuevo o escríbenos por WhatsApp.'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection;
